import { Injectable } from '@angular/core';
import { WindowRefService } from '../window/window-ref.service';
import { includes, trim } from 'lodash';
import {
  BrowserTypesEnum,
  DeviceTypesEnum,
  IBrowserInfo,
  IBrowserInfoHeaders,
  OSTypesEnum,
} from './browser-manager.data';

const userAgentParser = require('ua-parser-js');

@Injectable({
  providedIn: 'root',
})
export class BrowserManagerService {
  parsedUserAgent: any;
  navigator: any;

  constructor(private _windowRefService: WindowRefService) {
    this.navigator = this._windowRefService.nativeWindow.navigator;
    this.parsedUserAgent = userAgentParser(this.navigator.userAgent);
  }

  isMobile(): boolean {
    const deviceType: string = this.parsedUserAgent.device.type;

    return deviceType === DeviceTypesEnum.MOBILE;
  }

  isTablet(): boolean {
    const deviceType: string = this.parsedUserAgent.device.type;

    return deviceType === DeviceTypesEnum.TABLET;
  }

  getPlatform(): string {
    const os = this.parsedUserAgent.os;

    return trim([os.name, os.version || ''].join(' '));
  }

  isMacOS(): boolean {
    return includes(this.getPlatform().toLowerCase(), OSTypesEnum.MACOS);
  }

  getDevice(): string {
    const device = this.parsedUserAgent.device;

    if (!!device && !!device.vendor && !!device.model) {
      return device.vendor + '/' + device.model;
    } else {
      const browser = this.parsedUserAgent.browser;

      return trim([browser.name || '', browser.version || ''].join(' '));
    }
  }

  isOnline(): boolean {
    return this.navigator.onLine;
  }

  isIE(): boolean {
    const browserName = this.parsedUserAgent.browser.name;

    return (
      browserName === BrowserTypesEnum.IE ||
      browserName === BrowserTypesEnum.EDGE
    );
  }

  isFirefox(): boolean {
    const browserName = this.parsedUserAgent.browser.name;

    return browserName === BrowserTypesEnum.FIREFOX;
  }

  isIOS(): boolean {
    return this.parsedUserAgent.os === OSTypesEnum.IOS;
  }

  isAndroid(): boolean {
    return this.parsedUserAgent.os === OSTypesEnum.ANDROID;
  }

  // https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
  isInsideIframe(): boolean {
    try {
      return (
        this._windowRefService.nativeWindow.self !==
        this._windowRefService.nativeWindow.top
      );
    } catch (e) {
      return true;
    }
  }

  getBrowserData = (): IBrowserInfo => {
    return {
      isMobile: this.isMobile(),
      isMacOS: this.isMacOS(),
      isIOS: this.isIOS(),
      isAndroid: this.isAndroid(),
      isFirefox: this.isFirefox(),
      isIE: this.isIE(),
      isOnline: this.isOnline(),
      device: this.getDevice(),
      isTablet: this.isTablet(),
      platform: this.getPlatform(),
    };
  };

  getBrowserInfoHeaders = (
    version: string,
    deviceId: string
  ): IBrowserInfoHeaders => {
    return {
      'x-i2g-device': this.getDevice(),
      'x-i2g-device-id': deviceId,
      'x-i2g-platform': this.getPlatform(),
      'x-i2g-client': `Web/${version}`,
    };
  };
}
